import { ABI_VERSION } from "./generated";
import { WORKER_PROTOCOL_VERSION, isRenderWorkerOutboundEnvelope, isRenderWorkerOutboundMessage, } from "./worker-protocol";
/** Main-side lifecycle and handshake owner for one replaceable render Worker. */
export class RenderWorkerClient {
    #initializationTimeoutMs;
    #onClockMetrics;
    #onFatal;
    #onFrame;
    #onVirtualRefills;
    #onEditTransaction;
    #onStructureRequest;
    #onDocumentSelection;
    #onEventTransaction;
    #onNonPassiveRegions;
    #onEditingGeometry;
    #onSemantics;
    #onLayoutGeometry;
    #onPaintedText;
    #sessionId;
    #worker;
    #capabilities;
    #fatalError;
    #pending;
    #readyMode;
    #state = "created";
    constructor(worker, options) {
        this.#worker = worker;
        this.#sessionId = positiveU32(options.sessionId, "sessionId");
        this.#initializationTimeoutMs = boundedTimeout(options.initializationTimeoutMs ?? 10_000);
        this.#onClockMetrics = options.onClockMetrics;
        this.#onFatal = options.onFatal;
        this.#onFrame = options.onFrame;
        this.#onVirtualRefills = options.onVirtualRefills;
        this.#onEditTransaction = options.onEditTransaction;
        this.#onStructureRequest = options.onStructureRequest;
        this.#onDocumentSelection = options.onDocumentSelection;
        this.#onEventTransaction = options.onEventTransaction;
        this.#onNonPassiveRegions = options.onNonPassiveRegions;
        this.#onEditingGeometry = options.onEditingGeometry;
        this.#onSemantics = options.onSemantics;
        this.#onLayoutGeometry = options.onLayoutGeometry;
        this.#onPaintedText = options.onPaintedText;
        worker.addEventListener("message", this.#handleMessage);
        worker.addEventListener("error", this.#handleError);
        worker.addEventListener("messageerror", this.#handleMessageError);
    }
    get capabilities() {
        return this.#capabilities;
    }
    get endpoint() {
        return this.#worker;
    }
    get sessionId() {
        return this.#sessionId;
    }
    get state() {
        return this.#state;
    }
    async prepare() {
        this.requireState("created");
        this.#state = "preparing";
        const message = {
            abiVersion: ABI_VERSION,
            kind: "pingo:prepare",
            protocolVersion: WORKER_PROTOCOL_VERSION,
            sessionId: this.#sessionId,
        };
        try {
            await this.request("pingo:prepared", message);
        }
        catch (cause) {
            const error = toError(cause, "render Worker prepare failed");
            this.fail(error);
            throw error;
        }
        const capabilities = this.#capabilities;
        if (capabilities === undefined) {
            const error = new Error("render Worker omitted capabilities from prepare response");
            this.fail(error);
            throw error;
        }
        this.#state = "prepared";
        return capabilities;
    }
    async activate(activation) {
        this.requireState("prepared");
        const width = positiveFinite(activation.width, "Worker viewport width");
        const height = positiveFinite(activation.height, "Worker viewport height");
        if (activation.mode === "sab" &&
            (activation.ringBuffer === undefined || activation.inputRingBuffer === undefined)) {
            throw new Error("SAB Worker activation requires mutation and input ring buffers");
        }
        this.#state = "activating";
        this.#readyMode = undefined;
        const message = {
            canvas: activation.canvas,
            devicePixelRatio: positiveFinite(activation.devicePixelRatio, "Worker device pixel ratio"),
            height,
            incrementalPicturesEnabled: activation.incrementalPicturesEnabled ?? true,
            kind: "pingo:activate",
            mode: activation.mode,
            rasterCache: activation.rasterCache,
            reducedMotion: activation.reducedMotion,
            ...(activation.inputRingBuffer === undefined
                ? {}
                : { inputRingBuffer: activation.inputRingBuffer }),
            ...(activation.ringBuffer === undefined ? {} : { ringBuffer: activation.ringBuffer }),
            sessionId: this.#sessionId,
            ...(activation.targetFrameMs === undefined
                ? {}
                : { targetFrameMs: positiveFinite(activation.targetFrameMs, "target frame interval") }),
            width,
        };
        try {
            await this.request("pingo:ready", message, [activation.canvas]);
            if (this.#readyMode !== activation.mode) {
                throw new Error(`render Worker activated ${String(this.#readyMode)}, expected ${activation.mode}`);
            }
            this.#readyMode = undefined;
            this.#state = "ready";
        }
        catch (cause) {
            const error = toError(cause, "render Worker activation failed");
            this.fail(error);
            throw error;
        }
    }
    postClockAnchor(sequence, timestamp) {
        if (this.#state !== "ready")
            return;
        const message = {
            kind: "pingo:clock-anchor",
            sequence: positiveU32(sequence, "clock anchor sequence"),
            sessionId: this.#sessionId,
            timestamp: finite(timestamp, "clock anchor timestamp"),
        };
        this.#worker.postMessage(message);
    }
    /** Transfers one immutable Input Stream transaction to the active Worker. */
    postInput(bytes) {
        this.requireState("ready");
        const owned = bytes.slice();
        const message = {
            bytes: owned,
            kind: "pingo:input",
            sessionId: this.#sessionId,
        };
        this.#worker.postMessage(message, [owned.buffer]);
    }
    /** Tells the Worker the canvas changed size, in logical pixels. */
    postResize(width, height, devicePixelRatio) {
        this.requireState("ready");
        const message = {
            devicePixelRatio,
            height,
            kind: "pingo:resize",
            sessionId: this.#sessionId,
            width,
        };
        this.#worker.postMessage(message);
    }
    /** Wakes the Worker to drain committed Input Stream slots without copying payload bytes. */
    postInputWake() {
        this.requireState("ready");
        const message = {
            kind: "pingo:input-wake",
            sessionId: this.#sessionId,
        };
        this.#worker.postMessage(message);
    }
    /** Starts or stops the worker's per-frame geometry export. */
    postLayoutGeometryActive(active) {
        if (this.#state !== "ready")
            return;
        this.#worker.postMessage({
            kind: "pingo:layout-geometry-active",
            active,
            sessionId: this.#sessionId,
        });
    }
    /** Starts or stops the worker's per-frame painted-text export. */
    postPaintedTextActive(active) {
        if (this.#state !== "ready")
            return;
        this.#worker.postMessage({
            kind: "pingo:painted-text-active",
            active,
            sessionId: this.#sessionId,
        });
    }
    /** Propagates a live prefers-reduced-motion change to the active Core. */
    postReducedMotion(reduced) {
        this.requireState("ready");
        const message = {
            kind: "pingo:reduced-motion",
            reduced,
            sessionId: this.#sessionId,
        };
        this.#worker.postMessage(message);
    }
    /** Transfers ownership of one bounded live media frame to the render Worker. */
    postMediaFrame(resourceId, source, path) {
        try {
            this.requireState("ready");
            const message = {
                kind: "pingo:media-frame",
                path,
                resourceId: positiveU32(resourceId, "media resourceId"),
                sessionId: this.#sessionId,
                source,
            };
            this.#worker.postMessage(message, [source]);
        }
        catch (cause) {
            closeMediaSource(source);
            throw cause;
        }
    }
    async close() {
        if (this.#state === "closed")
            return;
        if (this.#state === "failed" || this.#state === "created") {
            this.terminate();
            return;
        }
        const message = {
            kind: "pingo:shutdown",
            sessionId: this.#sessionId,
        };
        try {
            await this.request("pingo:shutdown-complete", message);
        }
        catch {
            // Shutdown is best-effort; terminate is the bounded completion guarantee.
        }
        this.terminate();
    }
    terminate() {
        if (this.#state === "closed")
            return;
        this.rejectPending(new Error("render Worker terminated"));
        this.detach();
        this.#worker.terminate();
        this.#state = "closed";
    }
    #handleMessage = (event) => {
        const message = event.data;
        if (!isRenderWorkerOutboundMessage(message)) {
            if (isRenderWorkerOutboundEnvelope(message)) {
                const candidateSession = typeof message === "object" && message !== null
                    ? message.sessionId
                    : undefined;
                if (typeof candidateSession === "number" &&
                    Number.isInteger(candidateSession) &&
                    candidateSession !== this.#sessionId)
                    return;
                // Naming the kind is the difference between a message a operator can
                // act on and one that says only that something, somewhere, was wrong.
                const kind = typeof message === "object" && message !== null
                    ? message.kind
                    : undefined;
                this.fail(new Error(`render Worker response is malformed${typeof kind === "string" ? `: ${kind}` : ""}`));
            }
            return;
        }
        if (message.sessionId !== this.#sessionId)
            return;
        switch (message.kind) {
            case "pingo:prepared":
                this.#capabilities = message.capabilities;
                this.resolvePending(message.kind);
                return;
            case "pingo:ready":
                this.#readyMode = message.mode;
                this.resolvePending(message.kind);
                return;
            case "pingo:shutdown-complete":
                this.resolvePending(message.kind);
                return;
            case "pingo:frame":
                if (this.#state === "ready")
                    this.#onFrame?.(message.report);
                return;
            case "pingo:clock-metrics":
                if (this.#state === "ready")
                    this.#onClockMetrics?.(message.metrics);
                return;
            case "pingo:virtual-refill":
                if (this.#state === "ready")
                    this.#onVirtualRefills?.(message.requests);
                return;
            case "pingo:edit-transaction":
                if (this.#state === "ready")
                    this.#onEditTransaction?.(message.transaction);
                return;
            case "pingo:structure-request":
                if (this.#state === "ready")
                    this.#onStructureRequest?.(message.request);
                return;
            case "pingo:document-selection":
                if (this.#state === "ready")
                    this.#onDocumentSelection?.(message.report);
                return;
            case "pingo:event-transaction":
                if (this.#state === "ready")
                    this.#onEventTransaction?.(message.transaction);
                return;
            case "pingo:non-passive-regions":
                if (this.#state === "ready")
                    this.#onNonPassiveRegions?.(message.regions);
                return;
            case "pingo:editing-geometry":
                if (this.#state === "ready")
                    this.#onEditingGeometry?.(message.frame);
                return;
            case "pingo:semantics":
                if (this.#state === "ready")
                    this.#onSemantics?.(message.nodes);
                return;
            case "pingo:layout-geometry":
                if (this.#state === "ready")
                    this.#onLayoutGeometry?.(message.frame);
                return;
            case "pingo:painted-text":
                if (this.#state === "ready")
                    this.#onPaintedText?.(message.snapshot);
                return;
            case "pingo:fatal":
                this.fail(new Error(`render Worker failed: ${message.error}`));
        }
    };
    #handleError = (event) => {
        this.fail(new Error(event.message || "render Worker crashed", { cause: event.error }));
    };
    #handleMessageError = () => {
        this.fail(new Error("render Worker message could not be deserialized"));
    };
    request(expected, message, transfer = []) {
        if (this.#pending !== undefined)
            throw new Error("render Worker already has a pending request");
        return new Promise((resolve, reject) => {
            const timer = globalThis.setTimeout(() => {
                if (this.#pending?.expected !== expected)
                    return;
                this.#pending = undefined;
                reject(new Error(`render Worker ${expected} handshake timed out`));
            }, this.#initializationTimeoutMs);
            this.#pending = { expected, reject, resolve, timer };
            try {
                this.#worker.postMessage(message, transfer);
            }
            catch (cause) {
                clearTimeout(timer);
                this.#pending = undefined;
                reject(toError(cause, "render Worker message failed"));
            }
        });
    }
    resolvePending(kind) {
        const pending = this.#pending;
        if (pending === undefined || pending.expected !== kind) {
            this.fail(new Error(`unexpected render Worker response ${kind}`));
            return;
        }
        clearTimeout(pending.timer);
        this.#pending = undefined;
        pending.resolve();
    }
    rejectPending(error) {
        const pending = this.#pending;
        if (pending === undefined)
            return;
        clearTimeout(pending.timer);
        this.#pending = undefined;
        pending.reject(error);
    }
    requireState(expected) {
        if (this.#state !== expected) {
            throw this.#fatalError ?? new Error(`render Worker is ${this.#state}, expected ${expected}`);
        }
    }
    fail(error) {
        if (this.#state === "failed" || this.#state === "closed")
            return;
        this.#fatalError = error;
        this.#state = "failed";
        this.rejectPending(error);
        this.#onFatal?.(error);
    }
    detach() {
        this.#worker.removeEventListener("message", this.#handleMessage);
        this.#worker.removeEventListener("error", this.#handleError);
        this.#worker.removeEventListener("messageerror", this.#handleMessageError);
    }
}
function closeMediaSource(source) {
    const close = source.close;
    if (typeof close === "function")
        close.call(source);
}
/** Default bundler-resolved production Worker factory. */
export function createRenderWorker() {
    return new Worker(new URL("./render-worker", import.meta.url), {
        name: "pingo-render",
        type: "module",
    });
}
function boundedTimeout(value) {
    if (!Number.isFinite(value) || value < 1 || value > 60_000) {
        throw new RangeError("initializationTimeoutMs must be from 1 to 60000");
    }
    return value;
}
function positiveU32(value, label) {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff_ffff) {
        throw new RangeError(`${label} must be a positive u32`);
    }
    return value;
}
function finite(value, label) {
    if (!Number.isFinite(value))
        throw new RangeError(`${label} must be finite`);
    return value;
}
function positiveFinite(value, label) {
    if (!Number.isFinite(value) || value <= 0)
        throw new RangeError(`${label} must be positive`);
    return value;
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
//# sourceMappingURL=worker-client.js.map